import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-[#5B21B6] font-['Outfit']" data-testid="nav-logo">
            MenoPath
          </Link>
          <Link to="/">
            <button className="flex items-center gap-2 text-[#5B21B6] hover:text-[#4C1D95] transition-colors font-medium">
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1E1B4B] font-['Outfit'] mb-2">
              Terms of Service
            </h1>
            <p className="text-[#6B7280] mb-8">Last Updated: January 25, 2026</p>

            <div className="prose prose-lg max-w-none text-[#374151] space-y-6">
              <p>
                Welcome to <strong className="text-[#5B21B6]">Menopath.ai</strong> ("Menopath," "we," "us," or "our"). 
                Menopath provides technology-enabled services that facilitate access to laboratory testing insights, 
                present health data in an understandable format, and offer educational health-related insights. 
                Our Services are intended to support health awareness and informed decision-making.
              </p>
              
              <p>
                By accessing or using Menopath's website, mobile applications, or related services 
                (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). 
                If you do not agree, you must not use the Services.
              </p>
              
              <p>
                These Terms incorporate our <Link to="/privacy" className="text-[#5B21B6] underline hover:text-[#4C1D95]">Privacy Policy</Link>, 
                which governs how we collect and use personal information.
              </p>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">Eligibility</h2>
                <p>
                  You must be at least 18 years of age to use the Services. 
                  Menopath does not permit accounts for minors.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  No Medical Advice or Provider Relationship
                </h2>
                <p>
                  Menopath does not provide medical advice, diagnosis, or treatment. Content, data, insights, 
                  and AI-generated responses are provided for <strong>informational purposes only</strong> and 
                  are not a substitute for professional medical care.
                </p>
                <p className="mt-3">
                  Use of the Services does not create a doctor-patient relationship between you and 
                  Menopath or its affiliates. Any healthcare services you receive are provided solely 
                  by independent third-party providers.
                </p>
                <p className="mt-3 p-4 bg-[#FEF3C7] rounded-xl text-[#92400E] font-medium">
                  If you believe you are experiencing a medical emergency, contact emergency services immediately.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">AI-Enabled Features</h2>
                <p>
                  Menopath may offer AI-powered tools that generate automated responses based on user input. 
                  These tools:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Are not human and use algorithms to generate predictions</li>
                  <li>May generate inaccurate or incomplete information</li>
                  <li>Are not intended to provide medical advice</li>
                  <li>Should not be relied upon without independent verification from healthcare professionals</li>
                </ul>
                <p className="mt-3">
                  You are solely responsible for evaluating and using AI-generated content.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Services and Third Parties
                </h2>
                <p>
                  Menopath facilitates access to third-party laboratories, clinicians, and service providers. 
                  Menopath:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Does not control third-party services</li>
                  <li>Does not guarantee accuracy or outcomes of lab results</li>
                  <li>Is not responsible for third-party acts or omissions</li>
                </ul>
                <p className="mt-3">
                  Your engagement with third-party providers is voluntary and at your own risk.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">User Accounts</h2>
                <p>
                  You are responsible for maintaining the confidentiality of your account credentials 
                  and for all activity associated with your account. You must notify us immediately 
                  of any unauthorized use.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Payments and Subscriptions
                </h2>
                <p>
                  Certain Services may require payment or subscription enrollment. By purchasing Services, 
                  you authorize Menopath to charge your selected payment method. Subscriptions automatically 
                  renew unless cancelled in accordance with posted cancellation terms. Refunds are subject 
                  to our refund policy.
                </p>
                <p className="mt-3">
                  Menopath does not accept insurance and operates on a private-pay basis.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">Acceptable Use</h2>
                <p>You agree not to:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Misuse the Services</li>
                  <li>Submit false or misleading information</li>
                  <li>Attempt to disrupt or reverse engineer the Services</li>
                  <li>Use the Services for unlawful, harmful, or deceptive purposes</li>
                </ul>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Intellectual Property
                </h2>
                <p>
                  All content, software, and materials provided by Menopath are owned by Menopath 
                  or its licensors and are protected by intellectual property laws. No rights are 
                  granted except as expressly stated.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Disclaimer of Warranties
                </h2>
                <p>
                  The Services are provided <strong>"as is"</strong> and <strong>"as available."</strong> Menopath 
                  makes no warranties regarding the accuracy, reliability, availability, or suitability of the 
                  Services or content.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Limitation of Liability
                </h2>
                <p>
                  To the maximum extent permitted by law, Menopath is not liable for indirect, incidental, 
                  or consequential damages. Menopath's total liability shall not exceed the greater of $100 
                  or the amount you paid for the Services.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Arbitration and Dispute Resolution
                </h2>
                <p>
                  Most disputes must be resolved through binding arbitration rather than court litigation. 
                  Arbitration includes a waiver of jury trial and class actions. You may opt out of 
                  arbitration within 30 days of first acceptance as described in the arbitration process.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">Termination</h2>
                <p>
                  You may stop using the Services at any time. Menopath may suspend or terminate access 
                  if these Terms are violated.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Changes to These Terms
                </h2>
                <p>
                  Menopath may update these Terms from time to time. Continued use of the Services 
                  after changes are posted constitutes acceptance of the revised Terms.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Contact Information
                </h2>
                <p>
                  If you have questions about these Terms, please contact us at:{' '}
                  <a href="mailto:support@menopath.ai" className="text-[#5B21B6] underline hover:text-[#4C1D95]">
                    support@menopath.ai
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-[#1E1B4B] py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm">© 2026 MenoPath. All rights reserved.</p>
          <div className="mt-4 flex justify-center gap-6">
            <Link to="/terms" className="text-white/70 hover:text-white transition-colors text-sm">Terms of Service</Link>
            <Link to="/privacy" className="text-white/70 hover:text-white transition-colors text-sm">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
